/**
 * OAuth Providers Module
 *
 * Handles Google and Microsoft OAuth provider configuration and error handling
 */

import { GoogleAuthProvider, OAuthProvider, signInWithPopup, UserCredential } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { AppError, createAuthError } from '@/lib/error-handling';

// OAuth Provider Types
export type OAuthProviderType = 'google' | 'microsoft';

/**
 * Configure and execute Google OAuth sign-in
 */
export async function signInWithGoogleProvider(): Promise<UserCredential> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  provider.addScope('email');
  provider.addScope('profile');

  return signInWithPopup(auth, provider);
}

/**
 * Configure and execute Microsoft OAuth sign-in
 */
export async function signInWithMicrosoftProvider(): Promise<UserCredential> {
  const provider = new OAuthProvider('microsoft.com');
  provider.setCustomParameters({ prompt: 'select_account' });
  provider.addScope('email');
  provider.addScope('openid');
  provider.addScope('profile');

  return signInWithPopup(auth, provider);
}

/**
 * Handle OAuth-specific errors and convert to AppError
 */
export function handleOAuthError(
  error: { code?: string; message?: string },
  provider: OAuthProviderType
): AppError {
  const errorCode = error.code || error.message;

  if (errorCode === 'auth/popup-closed-by-user') {
    return createAuthError('Sign-in was cancelled. Please try again.');
  }
  if (errorCode === 'auth/popup-blocked') {
    return createAuthError('Popup was blocked. Please allow popups for this site.');
  }
  if (errorCode === 'auth/account-exists-with-different-credential') {
    return createAuthError(
      'An account already exists with this email using a different sign-in method.'
    );
  }
  if (errorCode === 'auth/operation-not-allowed') {
    return createAuthError(
      `${provider === 'google' ? 'Google' : 'Microsoft'} sign-in is not enabled. Please contact your administrator.`
    );
  }
  if (errorCode === 'auth/unauthorized-domain') {
    return createAuthError('This domain is not authorized for sign-in.');
  }
  if (errorCode === 'auth/network-request-failed') {
    return createAuthError('Network error. Please check your connection and try again.');
  }
  if (provider === 'microsoft' && errorCode?.includes('tenant')) {
    return createAuthError(
      'Microsoft sign-in failed. Please contact your administrator to verify your organization settings.'
    );
  }
  return createAuthError(
    `Sign-in with ${provider === 'google' ? 'Google' : 'Microsoft'} failed. Please try again.`
  );
}

/**
 * Extract tenant ID from invitation URL or localStorage
 */
export function extractTenantFromInvitation(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const tenantFromUrl = urlParams.get('tenantId');
  const tenantFromStorage = localStorage.getItem('invitationTenantId');
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const tenantFromHash = hashParams.get('tenantId');
  return tenantFromUrl || tenantFromStorage || tenantFromHash;
}
